/**
 * AddGame Component
 * 
 * This component provides a form interface for adding new games to the system.
 * It handles:
 * - Game metadata (name, title, description, etc.)
 * - File uploads (game file, featured image, icon, screenshots)
 * - Category selection with visual indicators
 * - Rich text editor for game descriptions
 * - Form validation and submission
 */

import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  addGame,
  allCategories,
} from "../../../redux/Slices/extSlice";
import { toast } from "react-toastify";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Button,
  TextField,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Box,
} from "@mui/material";
import { Editor } from "react-draft-wysiwyg";
import { convertToRaw, EditorState } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from "draftjs-to-html";

const AddGame = () => {
  // ========================================
  // Redux & Refs
  // ========================================
  const { categories } = useSelector((state) => state.ext); // Get categories from Redux store
  const formRef = useRef(null); // Reference to the form element for reset
  const dispatch = useDispatch(); // Redux dispatch function

  // ========================================
  // State Management
  // ========================================
  
  // Rich text editor state for game description
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  );
  
  // HTML content converted from editor state
  const [convertedContent, setConvertedContent] = useState(null);
  
  // Form fields state - contains all game data
  const [fields, setFields] = useState({
    _id: "",
    game_name: "",
    game_file: "",
    meta_title: "",
    meta_description: "",
    cat_arr: [], // Array of category IDs
    featured_img: "",
    icon_img: "",
    layout: "",
    screenshots: [],
    title: "",
    description: "",
    developer_name: "",
    release_date: "",
  });

  // ========================================
  // Effects
  // ========================================
  
  /**
   * Effect: Convert editor state to HTML whenever editor content changes
   * Updates both the description field and convertedContent state
   */
  useEffect(() => {
    const html = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    setFields((prevData) => ({
      ...prevData,
      description: html,
    }));
    setConvertedContent(html);
  }, [editorState]);

  /**
   * Effect: Fetch all categories on component mount
   * Used to populate the category dropdown selector
   */
  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(allCategories())
          .then((data) => {
            if (data.payload?.code === 200) {
              // Categories loaded successfully
            } else {
              toast.error("Something went wrong", {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 3000,
              });
            }
          })
          .catch((err) => {
            console.log(err, "err in all ext");
            toast.error("Something went wrong", {
              position: toast.POSITION.TOP_CENTER,
              autoClose: 3000,
            });
          });
      } catch (error) {
        console.error("Error fetching extensions:", error);
      }
    };

    fetchData();
  }, [dispatch]);

  // ========================================
  // Constants & Configuration
  // ========================================
  
  // Menu props for dropdown components
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  // ========================================
  // Event Handlers
  // ========================================
  
  /**
   * Handle text input changes for form fields
   * @param {Object} e - Event object from input change
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  /**
   * Handle layout selection change (Portrait/Landscape)
   * @param {Object} e - Event object from select change
   */
  const handleChangeLayout = (e) => {
    const { value } = e.target;
    setFields((prevFields) => ({
      ...prevFields,
      layout: value,
    }));
  };

  /**
   * Handle category selection change
   * Supports both string (comma-separated) and array formats
   * @param {Object} e - Event object from select change
   */
  const handleSelectChange = (e) => {
    const { value } = e.target;
    setFields({
      ...fields,
      cat_arr: typeof value === "string" ? value.split(",") : value,
    });
  };

  /**
   * Handle form submission
   * Validates required fields, prepares FormData, and dispatches addGame action
   * @param {Object} e - Form submit event
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();

      // Validation: Check if required fields are present
      if (!fields.game_file || !fields.featured_img) {
        return toast.info("Game file and featured img is required", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 3000,
        });
      }

      // Add regular text fields to FormData (excluding file fields and special cases)
      Object.keys(fields).forEach((key) => {
        if (
          key !== "screenshots" &&
          key !== "game_file" &&
          key !== "featured_img" &&
          key !== "icon_img" &&
          key !== "cat_arr" &&
          key !== "game_name"
        ) {
          formData.append(key, fields[key]);
        }
      });

      // Generate game link from game name
      formData.append(
        "game_link",
        `https://www.punogames.com/games/${fields.game_name
          .replace(/[^a-z0-9-_]+/gi, "_")
          .toLowerCase()}`
      );

      // Add game name separately
      if (fields.game_name) {
        formData.append("game_name", fields["game_name"]);
      }

      // Add category array as JSON string
      if (Array.isArray(fields.cat_arr)) {
        formData.append("cat_arr", JSON.stringify(fields.cat_arr));
      }

      // Add game file (zip archive)
      if (fields.game_file instanceof File) {
        formData.append("game_file", fields.game_file, fields.game_file.name);
      }

      // Add featured image
      if (fields.featured_img instanceof File) {
        formData.append(
          "featured_img",
          fields.featured_img,
          fields.featured_img.name
        );
      }

      // Add icon image
      if (fields.icon_img instanceof File) {
        formData.append("icon_img", fields.icon_img, fields.icon_img.name);
      }

      // Add screenshots as multiple files
      if (Array.isArray(fields.screenshots)) {
        fields.screenshots.forEach((screenshot) => {
          if (screenshot instanceof File) {
            formData.append(`screenshots`, screenshot);
          }
        });
      }

      // Dispatch the addGame action with FormData
      dispatch(addGame(formData)).then((data) => {
        if (data.payload.code === 200) {
          // Reset form on success
          if (formRef.current) {
            formRef.current.reset();
          }
          
          // Reset all state
          setFields({
            _id: "",
            game_name: "",
            game_file: "",
            meta_title: "",
            meta_description: "",
            cat_arr: [],
            featured_img: "",
            icon_img: "",
            layout: "",
            screenshots: [],
            title: "",
            description: "",
            developer_name: "",
            release_date: "",
          });
          setConvertedContent("");
          setEditorState(() => EditorState.createEmpty());
          
          toast.success("Game added successfully!", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 3000,
          });
        } else {
          toast.error("Something went wrong", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 3000,
          });
        }
      });
    } catch (error) {
      console.log(error, "error");
      toast.error("Something went wrong", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
      });
    }
  };

  // ========================================
  // Utility Functions
  // ========================================
  
  /**
   * Create object URL for image preview
   * @param {File} img - Image file object
   * @returns {string} Object URL for the image
   */
  const CreateObjectUrl = (img) => {
    const objectURL = URL.createObjectURL(img);
    return objectURL;
  };

  /**
   * Create a map of category IDs to category names for easy lookup
   * Handles both string and ObjectId formats
   */
  const categoryMap = React.useMemo(() => {
    const map = new Map();
    if (categories?.data && Array.isArray(categories.data)) {
      categories.data.forEach((cat) => {
        const idStr = String(cat._id);
        map.set(idStr, cat.cat_name);
        if (cat._id) {
          map.set(cat._id, cat.cat_name);
        }
      });
    }
    return map;
  }, [categories]);

  /**
   * Get category name from category ID
   * @param {string|ObjectId} categoryId - Category ID to look up
   * @returns {string} Category name or ID if not found
   */
  const getCategoryName = (categoryId) => {
    const idStr = String(categoryId);
    return categoryMap.get(categoryId) || categoryMap.get(idStr) || categoryId;
  };

  // ========================================
  // Render Functions
  // ========================================
  
  /**
   * Render appropriate input field based on field key
   * Handles special cases like categories, images, files, description, etc.
   * @param {string} key - Field name/key
   * @param {any} value - Field value
   * @returns {JSX.Element} Appropriate form field component
   */
  const renderField = (key, value) => {
    // Skip rendering _id field
    if (key === "_id") return null;

    // Meta Description Field - Multiline text input
    if (key === "meta_description") {
      return (
        <TextField
          key={key}
          required
          label="Meta Description"
          value={fields[key]}
          multiline
          rows={4}
          sx={{ width: "100%" }}
          name={key}
          onChange={handleChange}
        />
      );
    }

    // Layout Field - Select dropdown (Portrait/Landscape)
    if (key === "layout") {
      return (
        <Box sx={{ minWidth: 120 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Layout</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={fields.layout}
              label="Layout"
              onChange={handleChangeLayout}
            >
              <MenuItem value={"portrait"}>Portrait</MenuItem>
              <MenuItem value={"landscape"}>Landscape</MenuItem>
            </Select>
          </FormControl>
        </Box>
      );
    }

    // Screenshots Field - Multiple file upload with preview
    if (key === "screenshots") {
      return (
        <div
          key={key}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "10px",
          }}
        >
          <Button
            variant="contained"
            fullWidth
            component="label"
            sx={{
              backgroundColor: "#6A4FE0",
              "&:hover": { backgroundColor: "#5a43c5" },
              paddingY: "12px",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            Upload Screenshots
            <input
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={(e) => {
                const files = Array.from(e.target.files);
                setFields((prevFields) => ({
                  ...prevFields,
                  [key]: [...(prevFields[key] || []), ...files],
                }));
              }}
            />
          </Button>
          
          {/* Display uploaded screenshot previews */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            {fields[key] &&
              fields[key].map((file, index) => (
                <div
                  key={index}
                  style={{
                    width: "100px",
                    height: "100px",
                    position: "relative",
                  }}
                >
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Screenshot ${index + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <Button
                    size="small"
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                    }}
                    onClick={() => {
                      setFields((prevFields) => ({
                        ...prevFields,
                        [key]: prevFields[key].filter((_, i) => i !== index),
                      }));
                    }}
                  >
                    X
                  </Button>
                </div>
              ))}
          </div>
        </div>
      );
    }

    // Category Array Field - Multi-select with checkboxes and chips
    if (key === "cat_arr" && Array.isArray(value)) {
      return (
        <FormControl key={key} fullWidth>
          <InputLabel id="demo-multiple-chip-label">CATEGORIES</InputLabel>
          <Select
            labelId="demo-multiple-chip-label"
            id="demo-multiple-chip"
            multiple
            required
            value={fields.cat_arr || []}
            onChange={handleSelectChange}
            input={<OutlinedInput id="select-multiple-chip" label="CATEGORIES" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((categoryId) => {
                  const categoryName = getCategoryName(categoryId);
                  return (
                    <Chip
                      key={categoryId}
                      label={categoryName}
                      size="small"
                      sx={{
                        backgroundColor: "#6A4FE0",
                        color: "#fff",
                        "& .MuiChip-deleteIcon": {
                          color: "#fff",
                        },
                      }}
                    />
                  );
                })}
              </Box>
            )}
            MenuProps={MenuProps}
          >
            {categories?.data?.map((category) => {
              const isSelected = fields.cat_arr?.includes(category._id) || false;
              return (
                <MenuItem
                  key={category._id}
                  value={category._id}
                  sx={{
                    backgroundColor: isSelected ? "rgba(106, 79, 224, 0.08)" : "transparent",
                    "&:hover": {
                      backgroundColor: isSelected
                        ? "rgba(106, 79, 224, 0.12)"
                        : "rgba(0, 0, 0, 0.04)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "rgba(106, 79, 224, 0.12)",
                      "&:hover": {
                        backgroundColor: "rgba(106, 79, 224, 0.16)",
                      },
                    },
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    sx={{
                      color: "#6A4FE0",
                      "&.Mui-checked": {
                        color: "#6A4FE0",
                      },
                    }}
                  />
                  <ListItemText primary={category.cat_name} />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      );
    }

    // Image and File Fields - File upload with preview
    if (key.includes("img") || key.includes("file")) {
      return (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <Button
              variant="contained"
              fullWidth
              component="label"
              sx={{
                backgroundColor: "#6A4FE0",
                "&:hover": { backgroundColor: "#5a43c5" },
                paddingY: "12px",
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              Upload {key.replace(/_/g, " ").toUpperCase()}
              <input
                type="file"
                accept={key.includes("file") ? ".zip" : "image/*"}
                hidden
                onChange={(e) => {
                  if (key.includes("file")) {
                    // Validate zip file type
                    if (
                      e.target.files[0] &&
                      e.target.files[0].type === "application/x-zip-compressed"
                    ) {
                      setFields((prevData) => ({
                        ...prevData,
                        [key]: e.target.files[0],
                      }));
                    }
                  } else {
                    // Handle image files
                    setFields((prevData) => ({
                      ...prevData,
                      [key]: e.target.files[0],
                    }));
                  }
                }}
              />
            </Button>
            
            {/* Show file name chip for game_file */}
            {fields[key] && key.includes("file") && (
              <Chip
                label={fields.game_file.name}
                variant="outlined"
                onDelete={() =>
                  setFields((prevData) => ({
                    ...prevData,
                    [key]: "",
                  }))
                }
              />
            )}
          </div>

          {/* Show image preview for image fields */}
          {fields[key] && key.includes("img") && (
            <div
              style={{
                width: "100px",
                height: "100px",
                position: "relative",
                marginTop: "10px",
              }}
            >
              <img
                src={CreateObjectUrl(fields[key])}
                alt="Preview"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
              <Button
                size="small"
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                }}
                onClick={() => {
                  setFields((prevFields) => ({
                    ...prevFields,
                    [key]: "",
                  }));
                }}
              >
                X
              </Button>
            </div>
          )}
        </div>
      );
    }

    // Release Date Field - Date picker
    if (key.includes("release_date")) {
      return (
        <div style={{ marginBottom: "20px" }}>
          <TextField
            key={key}
            required
            type="date"
            value={fields[key]}
            defaultValue={new Date().toISOString().split("T")[0]}
            onChange={handleChange}
            sx={{ width: "100%" }}
            name={key}
          />
        </div>
      );
    }

    // Description Field - Rich text editor with HTML preview
    if (key.includes("description")) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            maxWidth: "90%",
          }}
        >
          <div style={{ width: "100%" }}>
            <p style={{ paddingLeft: "5px" }}>GAME DESCRIPTION</p>
            <Editor
              editorState={editorState}
              onEditorStateChange={setEditorState}
              wrapperClassName="wrapper-class"
              editorClassName="editor-class"
            />
          </div>
          <div style={{ width: "100%" }}>
            <p style={{ paddingLeft: "5px" }}>HTML</p>
            <div style={{ width: "100%", height: "100px" }}>
              <textarea
                style={{ width: "100%", height: "100px" }}
                value={convertedContent}
                placeholder="Type anything…"
                readOnly
              />
            </div>
          </div>
        </div>
      );
    }

    // Game Name Field - Text input with path preview
    if (key === "game_name") {
      return (
        <>
          <TextField
            key={key}
            required
            label={key.replace(/_/g, " ").toUpperCase()}
            value={fields[key]}
            sx={{ width: "100%" }}
            name={key}
            onChange={handleChange}
          />
          {fields[key] && (
            <p style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
              Your game path will be:{" "}
              {fields[key].replace(/[^a-z0-9-_]+/gi, "_").toLowerCase()}
            </p>
          )}
        </>
      );
    }

    // Default Field - Standard text input
    return (
      <TextField
        key={key}
        required
        label={key.replace(/_/g, " ").toUpperCase()}
        value={fields[key]}
        sx={{ width: "100%" }}
        name={key}
        onChange={handleChange}
      />
    );
  };

  // ========================================
  // Main Render
  // ========================================
  return (
    <Paper
      sx={{
        width: "75%",
        float: "right",
        marginTop: "6%",
        marginRight: "20px",
        padding: "30px",
        borderRadius: "12px",
      }}
    >
      <h2 style={{ textAlign: "left", marginBottom: "25px" }}>Add Game</h2>

      <form onSubmit={handleSubmit} ref={formRef}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "30px",
          }}
        >
          {/* Left Column - Basic game information */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "18px",
            }}
          >
            {renderField("game_name", fields.game_name)}
            {renderField("layout", fields.layout)}
            {renderField("title", fields.title)}
            {renderField("meta_title", fields.meta_title)}
            {renderField("meta_description", fields.meta_description)}
            {renderField("release_date", fields.release_date)}
            {renderField("cat_arr", fields.cat_arr)}
          </div>

          {/* Right Column - Description and media files */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "25px",
            }}
          >
            {renderField("description", fields.description)}

            {/* Media files grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
              }}
            >
              {renderField("game_file", fields.game_file)}
              {renderField("screenshots", fields.screenshots)}
              {renderField("featured_img", fields.featured_img)}
              {renderField("icon_img", fields.icon_img)}
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div style={{ marginTop: "40px", textAlign: "center" }}>
          <Button
            type="submit"
            variant="contained"
            sx={{
              width: "40%",
              padding: "12px",
              fontSize: "16px",
              backgroundColor: "#6A4FE0",
              "&:hover": { backgroundColor: "#583ccd" },
            }}
          >
            Submit
          </Button>
        </div>
      </form>
    </Paper>
  );
};

export default AddGame;
